import React, { useState, useEffect } from 'lib/react';
import {
  EVPaper, EVAlert, EVGrid, EVTypography,
  EVButton,
} from 'lib/components';
import { useParams, useLocation } from 'lib/router';
import Map from 'commonComponents/map';
import {
  RESIDENTIAL, COMMERCIAL, ERROR,
  LOADING,
  SUCCESS,
  STATUSES,
  USER_EVENTS,
} from 'constants/AppConstants';
import {
  getMapZoomForPoint,
  getStructureMapZoom,
  MAP_VIEW_CONTEXT,
} from 'commonComponents/map/mapView.utils';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { SUPPORT_ROUTE, ORDER_HISTORY_ROUTE } from 'constants/RouteConstants';
import { CLEAR_REPORT_DETAILS_DATA } from 'constants/ActionTypes';
import RetryScreen from 'commonComponents/retry-screen';
import history from 'store/CreateHistory';
import { openCancelOrderDialog } from 'features/cancel-order-dialog/CancelOrder.actions';
import { CancelOrderAlert } from 'features/orderHistory/components';
import updateReportAddressService from 'services/updateReportAddress.service';
import { formatAddress, stripUiAddressFlags } from 'features/order/Order.utils';
import { openOrderHistoryAlert } from 'features/orderHistory/OrderHistory.actions';
import { SUB_STATUSES } from 'features/orderHistory/OrderHistory.constants';
import { scrollToTop, trackUserEvent } from 'utils/app.utils';
import useMediaQuery from 'hooks/useMediaQuery';
import OrderResolvedScreen from 'commonComponents/orderResolvedScreen';
import { isAuthorized } from 'utils/auth.utils';
import Banner from 'commonComponents/banner';
import AddressVerificationStyles from './AddressVerificationStyles';
import AddressComponent from './components/AddressComponent';
import { loadAddressVerificationPage, updateAddressVerificationLatitudeLongitude } from './AddressVerification.actions';
import ValidationErrorDialogBox from './components/ValidationErrorDialogBox';
import AddressVerificationLoader from './components/AddressVerificationLoader';

function AddressVerification() {
  const classes = AddressVerificationStyles();
  const { reportId: encryptedReportId } = useParams();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const currentUrl = location.state?.currentUrl;
  const backUrl = currentUrl?.includes(ORDER_HISTORY_ROUTE) ? currentUrl : ORDER_HISTORY_ROUTE;
  const isMobile = useMediaQuery(640);
  const isTablet = useMediaQuery(1024);

  const {
    reportDetails, reportId, loading, addressVerificationMapPoint, loadError, mapError,
  } = useSelector(({ reportDetailsPage }) => (reportDetailsPage));

  useEffect(() => {
    dispatch(loadAddressVerificationPage(encryptedReportId));
    return () => dispatch({ type: CLEAR_REPORT_DETAILS_DATA });
  }, []);

  const [userMovedPin, setUserMovedPin] = useState(false);
  const [mapPinValidationError, setMapPinValidationError] = useState(false);
  const [showAddressOnPin, setShowAddressOnPin] = useState(null);
  const [updateAddressStatus, setUpdateAddressStatus] = useState(null);
  const [alertMessage, setAlertMessage] = useState('');

  const handleUpdatePin = (_, newPinLocation) => {
    setUserMovedPin(true);
    dispatch(updateAddressVerificationLatitudeLongitude(newPinLocation));
  };

  const handleFocusChange = () => {
    setShowAddressOnPin(0);
  };

  const handleCancelReport = () => {
    dispatch(openCancelOrderDialog(reportId, addressVerificationMapPoint));
  };

  const handleConfirmLocation = () => {
    if (!userMovedPin) {
      setMapPinValidationError(true);
    } else {
      setUpdateAddressStatus(LOADING);
      const requestBody = {
        building: addressVerificationMapPoint.building,
        ...stripUiAddressFlags(addressVerificationMapPoint.address),
      };

      updateReportAddressService(reportId, requestBody)
        .then(({ ok }) => {
          trackUserEvent(USER_EVENTS.VERIFY_ADDRESS);
          if (ok) {
            const status = SUCCESS;
            const message = t(
              'address_verification__identification_success',
              { reportId, reportAddress: formatAddress(addressVerificationMapPoint.address) },
            );
            if (isAuthorized()) {
              dispatch(openOrderHistoryAlert({
                severity: status,
                message,
              }));
              setUpdateAddressStatus(null);
              history.push(backUrl);
            } else {
              setUpdateAddressStatus(status);
              setAlertMessage(message);
            }
          } else {
            setUpdateAddressStatus(ERROR);
            setAlertMessage(t('address_verification__identification_failed', { reportId }));
          }
          scrollToTop();
        });
    }
  };

  if (reportDetails && !(Number(reportDetails?.statusID) === STATUSES.Pending && Number(reportDetails?.subStatusID) === SUB_STATUSES.NeedToID)) {
    return (
      <OrderResolvedScreen backToOrderHistoryUrl={backUrl} />
    );
  }

  if (loadError || mapError) {
    return <RetryScreen handleRetryButtonClick={() => { dispatch(loadAddressVerificationPage(encryptedReportId)); }} />;
  }

  return (
    <>
      {[ERROR, SUCCESS].includes(updateAddressStatus) && (
        <Banner
          severity={updateAddressStatus}
          onClose={() => setUpdateAddressStatus(null)}
          message={alertMessage}
        />
      )}
      {loading ? <AddressVerificationLoader />
        : (
          <EVGrid className={classes.addressVerificationContainer}>
            <CancelOrderAlert />
            <ValidationErrorDialogBox mapPinValidationError={mapPinValidationError} setMapPinValidationError={setMapPinValidationError} />
            <EVPaper elevation={0} className={classes.addressVerificationPaper}>
              <EVAlert severity="warning" className={classes.alertContainer}>
                <EVTypography style={{ color: '#1B202C', fontSize: '18px', fontWeight: 600 }}>
                  {t('address_verification__alert_msg__report_on_hold', { reportId })}
                </EVTypography>
                <EVTypography style={{ color: '#1B202C', fontSize: '16px', fontWeight: 400 }}>
                  {t('address_verification__alert_msg__check_pin_placement')}
                </EVTypography>
              </EVAlert>
              <EVGrid container className={classes.reportAddressContainer}>
                <EVTypography data-testid="report-type" className={classes.reportTypeText}>
                  <b>{t('address_verification__report_type')}</b>
                  {': '}
                  {reportDetails?.reportType}
                </EVTypography>
                <EVGrid
                  item
                  className={classes.addressComponentContainer}
                >
                  {addressVerificationMapPoint && (
                    <AddressComponent
                      buildingData={addressVerificationMapPoint}
                    />
                  )}
                </EVGrid>
              </EVGrid>
            </EVPaper>
            <EVPaper elevation={0} className={classes.mapPinVerificationPaper}>
              <EVGrid className={classes.mapContainer}>
                <EVAlert severity="info" className={classes.mapInfoAlert}>
                  <EVTypography className={classes.mapInfoAlertText}>
                    {t('address_verification__map__info_alert')}
                    {' '}
                    <b>{t('address_verification__map__comfirm_location')}</b>
                  </EVTypography>
                </EVAlert>
                <EVGrid className={classes.mapComponent}>
                  {addressVerificationMapPoint && (
                    <Map
                      center={addressVerificationMapPoint?.address}
                      mapPoints={[addressVerificationMapPoint]}
                      propertyType={reportDetails?.isReportMSO ? COMMERCIAL : RESIDENTIAL}
                      onUpdatePinLocation={handleUpdatePin}
                      mapOptions={{ showMapTypeSelector: false, showZoomButtons: true, showScalebar: true }}
                      onFocusChange={handleFocusChange}
                      focusedPinIndex={showAddressOnPin}
                      mapDisclaimerOffsetHeight={32}
                      defaultZoom={getMapZoomForPoint({
                        usedZipFallback: addressVerificationMapPoint?.usedZipFallback,
                        userMovedThePin: addressVerificationMapPoint?.userMovedThePin,
                        structureZoom: getStructureMapZoom(MAP_VIEW_CONTEXT.ADDRESS_VERIFICATION),
                      })}
                      pinDragEnabled
                    />
                  )}
                </EVGrid>
                <EVPaper
                  id={(isMobile || isTablet) ? 'map-disclaimer-mobile' : 'map-disclaimer'}
                  elevation={0}
                  className={classes.confirmLocationPaper}
                >
                  {(isAuthorized()) && (
                    <EVGrid>
                      <EVTypography className={classes.redirectionLinkText}>
                        {t('address_verification__map__need_help_contact')}
                        <EVButton onClick={() => history.push(SUPPORT_ROUTE)} className={classes.redirectionButtonStyles}>
                          {t('address_verification__map__customer_service')}
                        </EVButton>
                      </EVTypography>
                      <EVTypography className={classes.redirectionLinkText}>
                        {t('address_verification__map__no_longer_proceed')}
                        <EVButton
                          id="av-cancel-order-btn"
                          onClick={() => handleCancelReport()}
                          className={classes.redirectionButtonStyles}
                        >
                          {t('address_verification__map__cancel_order')}
                        </EVButton>
                      </EVTypography>
                    </EVGrid>
                  )}
                  <EVButton
                    data-testid="confirm-location"
                    onClick={handleConfirmLocation}
                    variant="contained"
                    color="primary"
                    diabled={updateAddressStatus === LOADING}
                    loading={updateAddressStatus === LOADING}
                  >
                    {t('address_verification__map__comfirm_location')}
                  </EVButton>
                </EVPaper>
              </EVGrid>
            </EVPaper>
          </EVGrid>
        )}
    </>
  );
}

export default AddressVerification;
